
import Vue from 'vue';
import Component from 'vue-class-component';
import { namespace } from 'vuex-class';
import { BoListPanel } from 'bonomo/dist';
import PaginationBar from '@/components/shared/PaginationBar.vue';
import Datim from '@/components/shared/Datim.vue';
import PinCodeDisplay from '@/components/shared/PinCodeDisplay.vue';
import CreateButton from '@/components/shared/CreateButton.vue';
import { BetterDataTableHeader } from '@/model/betterVuetifyInterfaces';
import { ISecurityCode, IShortCommunity } from '@/model/Community/communityInterfaces';
import { EPermissionActions } from '@/model/EPermissionActions';
import { IAuthUserData } from '@/store/modules/authModule';
import { PermissionHelper } from '@/helpers/PermissionHelper';
import { EUserPermissions } from '@/model/EUserPermissions';
import { securityCodeService } from '@/services/securityCodeService';

const communityModule = namespace('communityModule');
const authModule = namespace('authModule');

@Component({
  components: { BoListPanel, PaginationBar, Datim, PinCodeDisplay, CreateButton },
})
export default class SecurityCodeList extends Vue {
  @communityModule.Getter('securityCodeList')
  private securityCodeList!: ISecurityCode[];

  @communityModule.Getter('scCurrentPage')
  private scCurrentPage!: number;

  @communityModule.Getter('scLastPage')
  private scLastPage!: number;

  @communityModule.Getter('scTotal')
  private scTotal!: number;

  @communityModule.Getter('scPerPage')
  private scPerPage!: number;

  @communityModule.Getter('communityList')
  private communityList!: IShortCommunity[];

  @communityModule.Action('fetchSecurityCodeList')
  private fetchSecurityCodeList!: (filters: any) => Promise<any>;

  @communityModule.Action('clearSecurityCodeList')
  private clearSecurityCodeList!: () => void;

  @communityModule.Action('fetchAllCommunities')
  private fetchAllCommunities!: () => Promise<any>;

  @communityModule.Action('generateSecurityCode')
  private generateSecurityCode!: (communityId?: number) => Promise<string | null>;

  @authModule.Getter('userData')
  private userData!: IAuthUserData;

  private filterCommunityId: number | null = null;
  private selectedPerPage: number = 20;
  private generating: boolean = false;
  private pinModeEnabled: boolean = false;
  private activePinFromService: string | null = null;

  readonly perPageOptions: number[] = [20, 50, 100];

  get communityItems() {
    return (this.communityList || []).map(c => ({ text: c.name, value: c.id }));
  }

  get isDeveloper(): boolean {
    return PermissionHelper.hasPermissionAny(this.userData, EUserPermissions.PERMISSION_DEVELOPER)
      || PermissionHelper.hasPermissionAny(this.userData, EUserPermissions.PERMISSION_ADMINISTRATOR);
  }

  get canFilterByCommunity(): boolean {
    return this.$canAction(EPermissionActions.ACTION_COMMUNITIES) || this.isDeveloper;
  }

  get activePinCode(): string | null {
    return this.activePinFromService;
  }

  get headers(): BetterDataTableHeader[] {
    const cols: BetterDataTableHeader[] = [
      { text: this.$t('common.id') as string, align: 'start', sortable: false, value: 'id' },
      { text: this.$t('communities.securityCodes.pin') as string, align: 'start', sortable: false, value: 'code' },
    ];
    if (this.canFilterByCommunity) {
      cols.push({ text: this.$t('communities.securityCodes.community') as string, align: 'start', sortable: false, value: 'community_id' });
    }
    cols.push(
      { text: this.$t('communities.securityCodes.createdBy') as string, align: 'start', sortable: false, value: 'user_id' },
      { text: this.$t('common.createdAt') as string, align: 'start', sortable: false, value: 'created_at' },
    );
    return cols;
  }

  loadData() {
    if (this.canFilterByCommunity) {
      this.fetchAllCommunities().catch(() => {});
    }
    this.fetchSecurityCodeList(this.buildFilters());
  }

  mounted() {
    this.loadData();
    securityCodeService.myPin().then((result: {pin: string|null; community_id: number|null; community_name: string|null}) => {
      this.pinModeEnabled = !!(result && result.community_id);
      this.activePinFromService = result && result.pin ? result.pin : null;
    }).catch(() => {});
  }

  async onGenerate() {
    this.generating = true;
    try {
      const code = await this.generateSecurityCode();
      if (code) {
        this.activePinFromService = code;
      }
      this.clearSecurityCodeList();
      await this.fetchSecurityCodeList(this.buildFilters());
    } catch (e) {
      // error toast handled globally
    } finally {
      this.generating = false;
    }
  }

  private buildFilters(): any {
    const filters: any = { page: 1, per_page: this.selectedPerPage };
    if (this.filterCommunityId) {
      filters['communityId[]'] = this.filterCommunityId;
    }
    return filters;
  }

  onFilterChange() {
    this.clearSecurityCodeList();
    this.fetchSecurityCodeList(this.buildFilters());
  }

  onPageChange(page: number) {
    this.clearSecurityCodeList();
    const filters = this.buildFilters();
    filters.page = page;
    this.fetchSecurityCodeList(filters);
  }

  onPerPageChange(value: number) {
    this.selectedPerPage = value;
    this.clearSecurityCodeList();
    this.fetchSecurityCodeList(this.buildFilters());
  }
}
